import React, { useState, useRef, useEffect } from "react";
import "./StockSelectionDropdown.scss";

type StockSelectionDropdownProps = {
  stocks: { value: string; label: string }[];
  searchValue: string;
  setSearchValue: (val: string) => void;
  onSearchSubmit: () => void;
};

function StockSelectionDropdown({
  stocks,
  searchValue,
  setSearchValue,
  onSearchSubmit,
}: StockSelectionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredStocks = stocks.filter((stock) =>
    stock.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Reset activeIndex when search changes or dropdown opens/closes
  useEffect(() => {
    setActiveIndex(-1);
  }, [searchValue, isOpen]);

  // Scroll active item into view
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && listRef.current) {
      const activeEl = listRef.current.children[activeIndex] as HTMLElement;
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex, isOpen]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    setIsOpen(true);
  };

  const handleOptionClick = (stock: { value: string; label: string }) => {
    setSearchValue(stock.label);
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        setIsOpen(true);
        event.preventDefault();
      } else if (event.key === "Enter") {
        onSearchSubmit();
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((prev) =>
          filteredStocks.length > 0 ? (prev + 1) % filteredStocks.length : -1
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((prev) =>
          filteredStocks.length > 0
            ? (prev - 1 + filteredStocks.length) % filteredStocks.length
            : -1
        );
        break;
      case "Enter":
        event.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredStocks.length) {
          handleOptionClick(filteredStocks[activeIndex]);
        } else {
          onSearchSubmit();
          setIsOpen(false);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="stock-select" ref={dropdownRef}>
      <input
        className="stock-input-home"
        type="text"
        value={searchValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder="Search Ticker Name"
        onClick={() => setIsOpen(true)}
      />

      {isOpen && (
        <div className="stock-options">
          <ul className="py-1" ref={listRef}>
            {filteredStocks.length > 0 ? (
              filteredStocks.map((stock, index) => (
                <li
                  key={stock.value}
                  onClick={() => handleOptionClick(stock)}
                  className={`stock-option-item ${
                    index === activeIndex ? "active" : ""
                  }`}
                >
                  {stock.label}
                </li>
              ))
            ) : (
              <li className="no-results">No results found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default StockSelectionDropdown;
